/**
 * UIManager - Orchestrates all UI components for TitusJS
 * Simplified coordinator that manages PlayerWidget, SettingsPanel, and HighlightManager
 */
import "../css/titus.css";
import PlayerWidget from "./PlayerWidget.js";
import SettingsPanel from "./SettingsPanel.js";
import HighlightManager from "./HighlightManager.js";
import EventManager from "../core/EventManager.js";

export default class UIManager extends EventManager {
	/** @type {Object} UI configuration */
	#config = {
		position: "bottom-right",
		enableHighlighting: true,
		enableBanner: false,
	};

	/** @type {boolean} Whether UI is currently visible */
	#isVisible = false;

	/** @type {Object} Event callbacks registry */
	#callbacks = {
		onPlay: null,
		onPause: null,
		onNext: null,
		onPrevious: null,
		onClose: null,
		onSettingsChange: null,
	};

	/** @type {Object} UI settings */
	#settings = {
		highlightColor: "#ffeb3b",
		bannerColor: "#2196f3",
		showBanner: false,
		showHighlight: true,
	};

	/** @type {PlayerWidget} Player widget component */
	#playerWidget;

	/** @type {SettingsPanel} Settings panel component */
	#settingsPanel;

	/** @type {HighlightManager} Highlight manager component */
	#highlightManager;

	/**
	 * Initialize UIManager
	 * @param {Object} config - Configuration options
	 */
	constructor(config = {}) {
		super();
		this.#config = { ...this.#config, ...config };

		// Apply config settings first (from ConfigManager)
		if (config.enableHighlighting !== undefined) {
			this.#settings.showHighlight = config.enableHighlighting;
		}
		if (config.enableBanner !== undefined) {
			this.#settings.showBanner = config.enableBanner;
		}

		this.#settings = { ...this.#settings, ...this.#loadUISettings() };

		this.#initializeComponents();
	}

	/**
	 * Initialize UI components
	 * @private
	 */
	#initializeComponents() {
		this.#playerWidget = new PlayerWidget(
			(eventName, data) => this.#triggerCallback(eventName, data),
			() => this.#settingsPanel?.toggle(),
		);

		this.#settingsPanel = new SettingsPanel(
			this.#settings,
			(type, settings) => this.#handleSettingChange(type, settings),
		);

		this.#highlightManager = new HighlightManager(this.#settings);
	}

	/**
	 * Show the UI
	 * @param {Object} playerState - Initial player state
	 */
	show(playerState = {}) {
		if (this.#isVisible) return;

		const playerElement = this.#playerWidget.create(playerState);
		this.#applyPositionClass(playerElement);
		document.body.appendChild(playerElement);

		const settingsElement = this.#settingsPanel.create();
		this.#applyPositionClass(settingsElement);
		document.body.appendChild(settingsElement);

		this.#isVisible = true;
	}

	/**
	 * Hide the UI
	 */
	hide() {
		if (!this.#isVisible) return;

		this.#highlightManager.cleanup();
		this.#playerWidget.destroy();
		this.#settingsPanel.destroy();

		this.#isVisible = false;
	}

	/**
	 * Update player state
	 * @param {Object} state - New player state
	 */
	updatePlayerState(state) {
		this.#playerWidget.updateState(state);
	}

	/**
	 * Highlight a text segment
	 * @param {Element} element - Element to highlight
	 */
	highlightSegment(element) {
		this.#highlightManager.highlightSegment(element);
	}

	/**
	 * Remove current highlighting
	 */
	removeHighlighting() {
		this.#highlightManager.removeHighlighting();
	}

	/**
	 * Show text banner
	 * @param {string} text - Text to display
	 */
	showBanner(text) {
		this.#highlightManager.showBanner(text);
	}

	/**
	 * Hide text banner
	 */
	hideBanner() {
		this.#highlightManager.hideBanner();
	}

	/**
	 * Set event callback
	 * @param {string} eventName - Event name
	 * @param {Function} callback - Callback function
	 */
	setCallback(eventName, callback) {
		if (eventName in this.#callbacks) {
			this.#callbacks[eventName] = callback;
		}
	}

	/**
	 * Trigger a callback
	 * @param {string} eventName - Event name to trigger
	 * @param {*} data - Data to pass to callback
	 * @private
	 */
	#triggerCallback(eventName, data = null) {
		const callback = this.#callbacks[eventName];
		if (callback && typeof callback === "function") {
			try {
				callback(data);
			} catch (error) {
				console.error(
					`TitusJS: Error in ${eventName} callback:`,
					error,
				);
			}
		}
	}

	/**
	 * Handle setting changes from components
	 * @param {string} type - Type of setting ('speech' or 'ui')
	 * @param {Object} settings - Settings to update
	 * @private
	 */
	#handleSettingChange(type, settings) {
		if (type === "speech") {
			this.#triggerCallback("onSettingsChange", settings);
		} else if (type === "ui") {
			this.#settings = { ...this.#settings, ...settings };
			this.#saveUISettings();

			this.#highlightManager.settings = this.#settings;

			if (settings.bannerColor) {
				this.#highlightManager.updateBannerColor();
			}
			if (settings.highlightColor) {
				this.#highlightManager.updateHighlightColor();
			}

			if (settings.showBanner === false) {
				this.#highlightManager.hideBanner();
			}
			if (settings.showHighlight === false) {
				this.#highlightManager.removeHighlighting();
			}
		}
	}

	/**
	 * Update UI settings
	 * @param {Object} newSettings - Settings to update
	 */
	updateUISettings(newSettings) {
		this.#settings = { ...this.#settings, ...newSettings };
		this.#saveUISettings();
		this.#settingsPanel.updateSettings({ ui: this.#settings });
		this.#highlightManager.settings = this.#settings;
	}

	/**
	 * Load UI settings from localStorage
	 * @returns {Object} Saved settings
	 * @private
	 */
	#loadUISettings() {
		if (!this.#isLocalStorageAvailable()) return {};

		try {
			const saved = localStorage.getItem("titusjs-ui-settings");
			if (saved) {
				const parsed = JSON.parse(saved);
				if (
					typeof parsed === "object" &&
					parsed !== null &&
					!Array.isArray(parsed)
				) {
					return parsed;
				}
			}
			return {};
		} catch (error) {
			console.warn("TitusJS: Could not load UI settings:", error);
			return {};
		}
	}

	/**
	 * Save UI settings to localStorage
	 * @private
	 */
	#saveUISettings() {
		if (!this.#isLocalStorageAvailable()) {
			console.warn(
				"TitusJS: localStorage not available, settings will not persist",
			);
			return;
		}

		try {
			localStorage.setItem(
				"titusjs-ui-settings",
				JSON.stringify(this.#settings),
			);
		} catch (error) {
			if (error.name === "QuotaExceededError") {
				console.warn(
					"TitusJS: localStorage quota exceeded, could not save UI settings",
				);
			} else {
				console.warn("TitusJS: Could not save UI settings:", error);
			}
		}
	}

	/**
	 * Check if localStorage is available and working
	 * @returns {boolean} Whether localStorage is available
	 * @private
	 */
	#isLocalStorageAvailable() {
		try {
			const test = "__tjs_storage_test__";
			localStorage.setItem(test, test);
			localStorage.removeItem(test);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Apply position class to an element
	 * @param {HTMLElement} element - Element to apply position class to
	 * @private
	 */
	#applyPositionClass(element) {
		if (!element) return;
		const positionClass = `tjs-position-${this.#config.position}`;
		element.classList.add(positionClass);
	}

	/**
	 * Clean up resources
	 */
	destroy() {
		this.hide();
		this.clearAllListeners();
	}
}
